import React from 'react';
import { Box, Flex, HStack, Text, Badge, Button, IconButton, Wrap, WrapItem, Icon } from '@chakra-ui/react';
import { CloseIcon, RepeatIcon, WarningTwoIcon, TimeIcon, InfoIcon } from '@chakra-ui/icons';
import { agruparOcorrencias, resumoOcorrencias, podeTentarDeNovo } from './ocorrenciasImpressao';

const ICONE = {
    bloqueado: WarningTwoIcon,
    sem_danfe: InfoIcon,
    sem_etiqueta: InfoIcon,
    aguardando: TimeIcon,
    instabilidade: TimeIcon,
    outro: InfoIcon,
};

const PainelOcorrenciasImpressao = React.forwardRef(({ ocorrencias, titulo, parcial, onFechar, onTentarDeNovo, tentando }, ref) => {
    const grupos = agruparOcorrencias(ocorrencias);
    if (grupos.length === 0) return null;

    const { totalVendas } = resumoOcorrencias(grupos);
    const temBloqueio = grupos.some((g) => g.categoria === 'bloqueado' || g.categoria === 'sem_etiqueta');
    const soAviso = grupos.every((g) => g.categoria === 'sem_danfe');
    const corBorda = soAviso ? 'blue.400' : (temBloqueio ? 'red.400' : 'orange.400');
    const corFundo = soAviso ? 'blue.50' : (temBloqueio ? 'red.50' : 'orange.50');

    const cabecalho = titulo || (parcial
        ? 'Saiu parcial: algumas vendas ficaram de fora'
        : 'Nenhuma etiqueta saiu');

    return (
        <Box
            ref={ref}
            mb={4}
            bg={corFundo}
            borderWidth="1px"
            borderColor={corBorda}
            borderLeftWidth="5px"
            rounded="lg"
            p={{ base: 3, md: 4 }}
            shadow="sm"
        >
            <Flex justify="space-between" align="flex-start" gap={2} wrap="wrap" mb={3}>
                <Box minW={0} flex="1 1 220px">
                    <HStack spacing={2} align="center" wrap="wrap">
                        <Icon as={soAviso ? InfoIcon : (temBloqueio ? WarningTwoIcon : TimeIcon)} color={soAviso ? 'blue.500' : (temBloqueio ? 'red.500' : 'orange.500')} />
                        <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="bold" color={soAviso ? 'blue.700' : (temBloqueio ? 'red.700' : 'orange.700')}>
                            {cabecalho}
                        </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                        {soAviso
                            ? (totalVendas === 1 ? '1 venda saiu sem a DANFE' : `${totalVendas} vendas saíram sem a DANFE`)
                            : (totalVendas === 1 ? '1 venda não imprimiu' : `${totalVendas} vendas não imprimiram`)}
                        {parcial ? '. O arquivo com o que deu certo já foi baixado.' : '.'}
                    </Text>
                </Box>

                <HStack spacing={1} flexShrink={0}>
                    {onTentarDeNovo && podeTentarDeNovo(grupos) && (
                        <Button
                            size="xs"
                            colorScheme="orange"
                            leftIcon={<RepeatIcon />}
                            onClick={onTentarDeNovo}
                            isLoading={tentando}
                        >
                            Tentar de novo
                        </Button>
                    )}
                    {onFechar && (
                        <IconButton
                            aria-label="Fechar avisos"
                            icon={<CloseIcon boxSize={2.5} />}
                            size="xs"
                            variant="ghost"
                            onClick={onFechar}
                        />
                    )}
                </HStack>
            </Flex>

            <Flex direction="column" gap={2}>
                {grupos.map((g) => (
                    <Box key={g.chave} bg="white" rounded="md" borderWidth="1px" borderColor={`${g.cor}.200`} p={{ base: 2, md: 3 }}>
                        <Flex align="center" gap={2} wrap="wrap" mb={1}>
                            <Badge colorScheme={g.cor} borderRadius="full" px={2}>
                                <Icon as={ICONE[g.categoria] || InfoIcon} mr={1} boxSize={2.5} />
                                {g.vendas.length > 0
                                    ? `${g.vendas.length} ${g.vendas.length === 1 ? 'venda' : 'vendas'}`
                                    : 'aviso'}
                            </Badge>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">{g.titulo}</Text>
                        </Flex>

                        <Text fontSize="xs" color="gray.700" mb={g.vendas.length > 0 ? 2 : 1}>
                            {g.motivo}
                        </Text>

                        {g.vendas.length > 0 && (
                            <Wrap spacing={1} mb={2}>
                                {g.vendas.map((v) => (
                                    <WrapItem key={v}>
                                        <Badge variant="subtle" colorScheme="gray" fontSize="2xs" borderRadius="md" px={2} py={0.5}>
                                            #{v}
                                        </Badge>
                                    </WrapItem>
                                ))}
                            </Wrap>
                        )}

                        <Text fontSize="2xs" color={`${g.cor}.700`} fontWeight="medium">
                            {g.acao}
                        </Text>
                    </Box>
                ))}
            </Flex>
        </Box>
    );
});

PainelOcorrenciasImpressao.displayName = 'PainelOcorrenciasImpressao';

export default PainelOcorrenciasImpressao;
